// Hook + wrapper pra usar o PizzaPopup em qualquer módulo (Cardápio, Balcão, Mesas, Delivery).
// Encapsula: carregamento do pizzaConfig, merge com order_steps do produto, abertura/fechamento,
// e adaptação do item adicionado pro formato esperado pelo módulo (via onAddToCart callback).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import PizzaPopup from "./PizzaPopup";

const DEFAULT_CONFIG = { enabled: false, sizes: [], borders: [], doughs: [], flavors: [], pricing_rule: "highest" };

export function usePizzaPopup({ onAddToCart, allProducts = [], darkMode = false, clienteId = null, moduloOferta = "Cardapio" } = {}) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [open, setOpen] = useState(false);
  const [primaryFlavor, setPrimaryFlavor] = useState(null);

  // Detecta produto pizza e abre o popup; retorna true se interceptou.
  // Síncrono pra preservar a API existente (5 callers usam `if (tryOpenPizza)`).
  //
  // CAMINHO LEGADO DESATIVADO: o popup só abre se o produto tem order_steps
  // pizza_* configurados (caminho NOVO via aba Personalização do produto-mãe).
  // Backend retorna source="product_steps" só nesse caso. Se vier outro source
  // (ou nenhum), mostramos toast pedindo configurar Personalização e não abrimos.
  const tryOpenPizza = useCallback((product) => {
    if (!product || product.product_type !== "pizza") return false;

    // Abre popup imediato com primaryFlavor (UI mostra estado vazio enquanto
    // config carrega). Reset config pra DEFAULT antes do fetch, pra evitar
    // dados cacheados de pizza anterior aparecerem brevemente.
    setPrimaryFlavor({ ...product, order_steps: product.order_steps || [] });
    setConfig(DEFAULT_CONFIG);
    setOpen(true);

    // Fetch product-aware obrigatório.
    axios
      // clienteId (Campanha Inteligente): as ofertas de pizza do cliente vêm aplicadas por tamanho.
      .get(`/api/public/pizza/config?product_id=${encodeURIComponent(product.id)}${clienteId ? `&cliente_id=${encodeURIComponent(clienteId)}&modulo=${encodeURIComponent(moduloOferta)}` : ""}`, { timeout: 5000 })
      .then((res) => {
        if (res?.data?.source === "product_steps") {
          setConfig(res.data);
          // Enriquece primaryFlavor com `pizza_prices` (da matriz prices_by_size
          // do item DELE em pizza_flavor). PizzaPopup usa pra mostrar valor por
          // tamanho no card da etapa "Tamanho".
          const own = (res.data.flavors || []).find(f => f.id === product.id);
          if (own?.pizza_prices) {
            // `pizza_prices_original` + `promo_id` vêm quando há promoção (fixa ou
            // Campanha Inteligente) no tamanho: o popup mostra o preço cheio riscado.
            setPrimaryFlavor(prev => prev ? {
              ...prev,
              pizza_prices: own.pizza_prices,
              pizza_prices_original: own.pizza_prices_original || null,
              promo_id: own.promo_id || null,
            } : prev);
          }
        } else {
          toast.error(
            `Pizza "${product.name}" não foi configurada em Personalização. ` +
            `Acesse Cardápio → Produtos → ${product.name} → aba Personalização.`
          );
          setOpen(false);
          setPrimaryFlavor(null);
        }
      })
      .catch((e) => {
        toast.error(`Erro ao carregar pizza: ${e?.message || "rede indisponível"}`);
        setOpen(false);
        setPrimaryFlavor(null);
      });

    return true;
  }, [clienteId, moduloOferta]);

  const close = useCallback(() => {
    setOpen(false);
    setPrimaryFlavor(null);
  }, []);

  const handleAdd = useCallback((item) => {
    if (onAddToCart) onAddToCart(item);
    close();
  }, [onAddToCart, close]);

  // Renderiza num <dialog> HTML nativo via showModal() — coloca no "top layer" do browser,
  // que é imune a focus traps e pointer-events: none de Radix Dialogs ancestrais.
  // Sem isso, em Mesas/Delivery o Textarea de observação não recebia foco e cliques
  // eram bloqueados (focus trap roubava o foco; pointer-events: none capturava clicks).
  useEffect(() => {
    if (typeof document === "undefined") return;
    // Injeta CSS pra ocultar o backdrop nativo do <dialog> (uma vez)
    if (!document.getElementById("pizza-popup-style")) {
      const style = document.createElement("style");
      style.id = "pizza-popup-style";
      style.textContent = `
        dialog#pizza-popup-root::backdrop { background: transparent; }
        dialog#pizza-popup-root { padding: 0; border: 0; background: transparent;
          width: 100vw; height: 100vh; max-width: 100vw; max-height: 100vh; overflow: visible; }
      `;
      document.head.appendChild(style);
    }
    let dlg = document.getElementById("pizza-popup-root");
    if (!dlg) {
      dlg = document.createElement("dialog");
      dlg.id = "pizza-popup-root";
      // ESC do <dialog> nativo cancelaria; impedimos pra deixar o controle no React
      dlg.addEventListener("cancel", (e) => e.preventDefault());
      document.body.appendChild(dlg);
    }
    if (open && !dlg.open) {
      try { dlg.showModal(); } catch { /* já aberto */ }
    } else if (!open && dlg.open) {
      try { dlg.close(); } catch { /* já fechado */ }
    }
  }, [open]);

  const popup = useMemo(() => {
    if (typeof document === "undefined" || !open) return null;
    const dlg = document.getElementById("pizza-popup-root");
    if (!dlg) return null;
    return createPortal(
      <PizzaPopup
        open={open}
        onClose={close}
        primaryFlavor={primaryFlavor}
        allFlavors={config.flavors || []}
        allProducts={allProducts}
        config={config}
        darkMode={darkMode}
        onAddToCart={handleAdd}
      />,
      dlg
    );
  }, [open, primaryFlavor, config, allProducts, darkMode, handleAdd, close]);

  return { tryOpenPizza, popup, config, isOpen: open };
}
